import { useEffect, useState } from "react";

import { ApiError, apiClient } from "../../api/client";
import type { ThemeSetting } from "../../api/types";
import { listThemes } from "../../themes";
import { useTheme } from "../../themes/ThemeProvider";

interface State {
  status: "loading" | "ready" | "error";
  active: Record<string, boolean>;
  errorMessage: string | null;
}

const initial: State = {
  status: "loading",
  active: {},
  errorMessage: null
};

export function Themes() {
  const { refreshThemes } = useTheme();
  const [state, setState] = useState<State>(initial);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const response = await apiClient.listThemes();
        if (cancelled) {
          return;
        }
        setState({
          status: "ready",
          active: indexByID(response.themes),
          errorMessage: null
        });
      } catch (caught) {
        if (cancelled) {
          return;
        }
        setState({ status: "error", active: {}, errorMessage: messageFor(caught) });
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  async function toggle(themeID: string, isActive: boolean) {
    setSavingId(themeID);
    setActionError(null);
    try {
      const updated = await apiClient.setThemeActive(themeID, isActive);
      setState((previous) => ({
        ...previous,
        active: { ...previous.active, [updated.id]: updated.is_active }
      }));
      await refreshThemes();
    } catch (caught) {
      setActionError(messageFor(caught));
    } finally {
      setSavingId(null);
    }
  }

  if (state.status === "loading") {
    return <p className="loading">Loading themes…</p>;
  }
  if (state.status === "error") {
    return (
      <div className="card">
        <h2>Couldn't load themes</h2>
        <p>{state.errorMessage}</p>
      </div>
    );
  }

  const themes = listThemes();
  const activeCount = themes.filter((option) => state.active[option.id] ?? true).length;
  const lastActive = activeCount <= 1;

  return (
    <div className="themes-view">
      <header className="card">
        <h2>Themes</h2>
        <p className="muted">
          {activeCount} active · {themes.length - activeCount} hidden. Inactive themes are hidden
          from every user in this workspace, including you.
        </p>
      </header>

      {actionError !== null && (
        <div className="card error-banner" role="alert">
          <p>{actionError}</p>
        </div>
      )}

      <section className="card">
        <table className="people-table">
          <thead>
            <tr>
              <th className="people-table__th people-table__th--left">Theme</th>
              <th className="people-table__th people-table__th--left">Tagline</th>
              <th className="people-table__th people-table__th--right">Visible to users</th>
            </tr>
          </thead>
          <tbody>
            {themes.map((option) => {
              const isActive = state.active[option.id] ?? true;
              const saving = savingId === option.id;
              const lockedOn = isActive && lastActive;
              return (
                <tr key={option.id}>
                  <td>
                    <strong>{option.name}</strong>
                  </td>
                  <td className="muted">{option.tagline}</td>
                  <td className="people-table__num">
                    <label
                      className="theme-toggle"
                      title={lockedOn ? "At least one theme must stay active." : undefined}
                    >
                      <input
                        type="checkbox"
                        checked={isActive}
                        disabled={saving || lockedOn}
                        onChange={(event) => void toggle(option.id, event.target.checked)}
                      />
                      <span>{isActive ? "Active" : "Inactive"}</span>
                    </label>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function indexByID(entries: ThemeSetting[]): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const entry of entries) {
    out[entry.id] = entry.is_active;
  }
  return out;
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) {
    return caught.message;
  }
  return caught instanceof Error ? caught.message : "Could not load themes.";
}
